import './BatProfileBars.css'

/**
 * Unik styrkeprofil-visualisering: horisontale barre (ikke radar).
 * Viser Kontrol, Power, Manøvredygtighed, Spin og Sværhedsgrad.
 */
const ATTRIBUTES = [
  { key: 'control', label: 'Kontrol', color: 'var(--profile-control, #6366f1)' },
  { key: 'power', label: 'Power', color: 'var(--profile-power, #dc2626)' },
  { key: 'maneuverability', label: 'Manøvredygtighed', color: 'var(--profile-maneuver, #059669)' },
  { key: 'spin', label: 'Spin', color: 'var(--profile-spin, #d97706)' },
  { key: 'difficulty', label: 'Sværhedsgrad', color: 'var(--profile-difficulty, #7c3aed)' },
]

function deriveScores(specifications) {
  const spec = specifications || {}
  const balance = (spec.balance || spec.balance_point || '').toString().toLowerCase()
  const shape = (spec.shape || spec.form || '').toString().toLowerCase()
  const core = (spec.core || '').toString().toLowerCase()
  const weight = (spec.weight || spec.vægt || '').toString()

  let power = 5
  let control = 5
  let maneuverability = 5
  let spin = 5
  let difficulty = 5

  if (balance.includes('head heavy') || balance.includes('head-heavy')) {
    power = 8
    control = 4
    difficulty = 7
  } else if (balance.includes('handle') || balance.includes('low')) {
    control = 7
    power = 4
    maneuverability = 8
  } else if (balance.includes('even') || balance.includes('balanced') || balance.includes('mid')) {
    power = 6
    control = 6
  }

  if (shape.includes('diamond')) {
    power = Math.min(9, power + 1)
    difficulty = Math.min(9, difficulty + 1)
  } else if (shape.includes('round')) {
    control = Math.min(9, control + 1)
    difficulty = Math.max(3, difficulty - 1)
  } else if (shape.includes('teardrop') || shape.includes('dråbe') || shape.includes('hybrid')) {
    power = Math.min(8, power + 0.5)
    control = Math.min(8, control + 0.5)
  }

  if (core.includes('hard') || core.includes('hård')) {
    power = Math.min(9, power + 1)
    spin = Math.min(8, spin + 1)
  } else if (core.includes('soft') || core.includes('blød')) {
    control = Math.min(9, control + 1)
  }

  const nums = String(weight).match(/\d+(?:[.,]\d+)?/g) || []
  const vals = nums
    .map((n) => parseFloat(n.replace(',', '.')))
    .filter((n) => n >= 200 && n <= 500)
  const weightNum = vals.length
    ? vals.reduce((a, b) => a + b, 0) / vals.length
    : NaN
  if (!isNaN(weightNum)) {
    if (weightNum < 360) maneuverability = Math.min(9, maneuverability + 1)
    else if (weightNum > 375) power = Math.min(9, power + 0.5)
  }

  return {
    control: Math.round(Math.min(10, Math.max(1, control))),
    power: Math.round(Math.min(10, Math.max(1, power))),
    maneuverability: Math.round(Math.min(10, Math.max(1, maneuverability))),
    spin: Math.round(Math.min(10, Math.max(1, spin))),
    difficulty: Math.round(Math.min(10, Math.max(1, difficulty))),
  }
}

export function getBatProfile(specifications) {
  return deriveScores(specifications)
}

/** True hvis vi har en troværdig profil (ikke bare alle 5). */
function isProfileAssessed(scores) {
  if (!scores || typeof scores.control !== 'number') return false
  const vals = [scores.control, scores.power, scores.maneuverability, scores.spin, scores.difficulty]
  return vals.some((v) => v !== 5)
}

export function BatProfileBars({ specifications, profileScores, className = '' }) {
  const fromApi = profileScores && typeof profileScores.control === 'number'
  const scores = fromApi ? profileScores : deriveScores(specifications || {})
  const assessed = isProfileAssessed(scores)

  return (
    <section className={`bat-profile-bars ${className}`}>
      <h3 className="bat-profile-title">Styrkeprofil</h3>
      {assessed ? (
        <>
          <p className="bat-profile-desc">
            Vurderet ud fra specifikationer (vægt, balance, form, kerne) – sammenlignbart på tværs af bat.
          </p>
          <div className="bat-profile-list">
            {ATTRIBUTES.map(({ key, label, color }) => {
              const value = scores[key] ?? 5
              return (
                <div key={key} className="bat-profile-row">
                  <span className="bat-profile-label" style={{ color }}>{label}</span>
                  <div className="bat-profile-track">
                    <div
                      className="bat-profile-fill"
                      style={{ width: `${value * 10}%`, backgroundColor: color }}
                    />
                    <span className="bat-profile-value">{value}/10</span>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <p className="bat-profile-not-assessed">
          Styrkeprofil er endnu ikke vurderet. Vi viser kun en profil, når vi har nok specifikationer (vægt, balance, form, kerne) til en troværdig vurdering.
        </p>
      )}
    </section>
  )
}
